import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  getNextDueUpcomingWarning,
  getUpcomingWarningPollIntervalMs,
  markUpcomingWarningShown,
  pickNearestUpcomingMaintenance,
} from '../utils/maintenanceWarnings';
import { computeMinutesUntilStart } from '../utils/maintenanceBlock';

function warningKey(warning) {
  if (!warning?.maintenance?.id || !warning?.thresholdMinutes) return null;
  return `${warning.maintenance.id}:${warning.thresholdMinutes}`;
}

const useMaintenanceUpcomingWarnings = ({ enabled, tokenKey }) => {
  const [activeWarning, setActiveWarning] = useState(null);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const shownWarningKeyRef = useRef(null);

  const presentWarning = useCallback((warning) => {
    if (!warning) return;

    const nextKey = warningKey(warning);
    if (!nextKey || nextKey === shownWarningKeyRef.current) return;

    markUpcomingWarningShown(warning.maintenance.id, warning.thresholdMinutes);
    shownWarningKeyRef.current = nextKey;
    setActiveWarning(warning);
    setNoticeOpen(true);
  }, []);

  const handleNoticeClose = useCallback((_event, reason) => {
    if (reason === 'clickaway') return;
    setNoticeOpen(false);
    setActiveWarning(null);
    shownWarningKeyRef.current = null;
  }, []);

  useEffect(() => {
    if (!enabled) {
      shownWarningKeyRef.current = null;
      setNoticeOpen(false);
      setActiveWarning(null);
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;

    const scheduleNextPoll = (delayMs) => {
      if (cancelled) return;
      timeoutId = window.setTimeout(runPoll, delayMs);
    };

    const runPoll = async () => {
      const token = localStorage.getItem(tokenKey);
      if (!token || cancelled) {
        scheduleNextPoll(60000);
        return;
      }

      let nextIntervalMs = 60000;

      try {
        const { data } = await axios.get('/api/maintenance/scheduled', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        const upcoming = pickNearestUpcomingMaintenance(data);
        if (upcoming) {
          const minutesUntilStart = computeMinutesUntilStart(upcoming);
          nextIntervalMs = getUpcomingWarningPollIntervalMs(minutesUntilStart);

          const dueWarning = getNextDueUpcomingWarning(upcoming);
          if (dueWarning) {
            presentWarning(dueWarning);
          }
        }
      } catch (_e) {
        // ignore — maintenance block / network errors handled elsewhere
      }

      scheduleNextPoll(nextIntervalMs);
    };

    runPoll();

    return () => {
      cancelled = true;
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [enabled, tokenKey, presentWarning]);

  return {
    activeWarning,
    noticeOpen,
    handleNoticeClose,
  };
};

export default useMaintenanceUpcomingWarnings;
